/**
 * Google Identity Services, loaded on demand.
 *
 * Only pages that actually show a Google button pull the script in, so a
 * workspace that does not use Google never ships it. `window.google` is typed
 * loosely — GIS has no bundled types.
 */

// eslint-disable-next-line @typescript-eslint/no-explicit-any
declare global { interface Window { google?: any } }

const GSI_SRC = 'https://accounts.google.com/gsi/client';

let gsiPromise: Promise<void> | null = null;

/** Inject the GIS script once; resolve when window.google.accounts.id exists. */
export function loadGsi(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.google?.accounts?.id) return Promise.resolve();
  if (gsiPromise) return gsiPromise;
  gsiPromise = new Promise<void>((resolve, reject) => {
    const s = document.createElement('script');
    s.src = GSI_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => { gsiPromise = null; reject(new Error('Failed to load Google Sign-In.')); };
    document.head.appendChild(s);
  });
  return gsiPromise;
}

/**
 * Initialise GIS for `clientId` and render its button into `el`.
 * `onCredential` receives the Google ID token (a JWT) after the user picks
 * an account. Returns false when GIS could not be initialised.
 */
export async function renderGoogleButton(
  el: HTMLElement,
  clientId: string,
  onCredential: (credential: string) => void,
  opts: { text?: 'signin_with' | 'signup_with' | 'continue_with'; width?: number } = {},
): Promise<boolean> {
  await loadGsi();
  if (!window.google?.accounts?.id) return false;
  window.google.accounts.id.initialize({
    client_id: clientId,
    callback: (resp: { credential?: string }) => {
      if (resp?.credential) onCredential(resp.credential);
    },
  });
  el.innerHTML = '';
  window.google.accounts.id.renderButton(el, {
    theme: 'outline', size: 'large', width: opts.width ?? 372, text: opts.text ?? 'signin_with',
  });
  return true;
}
